import { errorMessage } from '@runnerboy/api-client';
import { CLOSED_STATUSES, CUSTOMER_PROGRESS_STEPS, progressIndex } from '@runnerboy/domain';
import type { OrderDetailDto, OrderStatus, OrderSummaryDto, RealtimeEvents, TrackingSnapshotDto } from '@runnerboy/types';
import { Alert, Badge, Button, Card, cx, EmptyState, inr, LiveMap, PageHeader, ProductImage, Sheet, Skeleton, Spinner, STATUS_TONE, Textarea, useToast, volume } from '@runnerboy/ui';
import { ArrowLeft, ArrowRight, Bike, CheckCircle2, ChevronRight, CircleAlert, FlaskConical, LifeBuoy, Lock, MessageCircle, Receipt, ShieldCheck, Star, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { io, type Socket } from 'socket.io-client';
import { apiOrigin } from '../lib/native';
import { StorefrontArt } from '../components/art';
import { BillDetails, ProgressTimeline, stageStyle } from '../components/commerce';
import { Page } from '../components/shell';
import { FlowHeader } from './checkout';
import { client, useAsync, useSession } from '../lib/session';

/** Accepted ID documents as people write them (enum values like VOTER_ID read badly when lower-cased). */
const ID_LABELS: Record<string, string> = { DRIVING_LICENCE: 'driving licence', PASSPORT: 'passport', VOTER_ID: 'Voter ID', AADHAAR: 'Aadhaar', PAN: 'PAN card' };
const idLabel = (d: string) => ID_LABELS[d] ?? d.replace(/_/g, ' ').toLowerCase();


// ---- Payment ------------------------------------------------------------------------------------------------
interface PaymentStatus {
  status: string;
  provider: string;
  method: string;
  amountMinor: number;
  clientPayload: { provider?: string; providerOrderId?: string; key?: string; order_id?: string } | null;
  testMode: boolean;
  failureReason: string | null;
}

export function PayPage() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const pay = useAsync((s) => client.get<PaymentStatus>(`/payments/${orderId}`, s), [orderId]);
  const order = useAsync((s) => client.get<OrderDetailDto>(`/orders/${orderId}`, s), [orderId]);

  useEffect(() => {
    const id = setInterval(async () => {
      const o = await client.get<OrderDetailDto>(`/orders/${orderId}`).catch(() => null);
      if (!o) return;
      order.setData(o);
      if (o.status !== 'PAYMENT_PENDING') {
        clearInterval(id);
        if (o.status !== 'FAILED') nav(`/orders/${orderId}`, { replace: true });
      }
    }, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const simulate = async (outcome: 'success' | 'failure') => {
    const providerOrderId = pay.data?.clientPayload?.providerOrderId;
    if (!providerOrderId) return;
    setBusy(outcome);
    try {
      await client.post(`/dev/mock-gateway/${providerOrderId}/complete`, { outcome });
    } catch (e) {
      toast('danger', errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const failed = order.data?.status === 'FAILED';
  const mock = pay.data?.provider === 'mock' || pay.data?.clientPayload?.provider === 'mock';
  return (
    <Page flow>
      <FlowHeader title={t('pay.title')} subtitle={order.data?.storeName} step="pay" onBack={() => nav('/orders', { replace: true })} />
      {pay.loading ? (
        <Skeleton className="mt-3 h-64 rounded-[28px]" />
      ) : failed ? (
        <div className="mt-3 rounded-[28px] bg-white p-6 text-center shadow-[var(--shadow-card)]">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-danger-50 text-danger-600">
            <XCircle className="h-8 w-8" aria-hidden />
          </span>
          <h2 className="rb-display mt-4 text-[22px] font-bold text-ink-900">{t('pay.failed')}</h2>
          {pay.data?.failureReason && <p className="mt-1 text-[13.5px] text-ink-500">{pay.data.failureReason}</p>}
          <Button className="mt-5" variant="accent" size="lg" block onClick={() => nav('/cart')}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <>
          <section className="relative mt-3 overflow-hidden rounded-[28px] bg-night-900 p-6 text-white shadow-[var(--shadow-float)]">
            <span className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-marigold-500/20 blur-3xl" aria-hidden />
            <div className="rb-eyebrow text-white/55">{order.data?.code ?? '—'}</div>
            <div className="rb-display rb-num mt-2 text-[44px] font-extrabold leading-none tracking-tight">{inr(pay.data?.amountMinor ?? 0)}</div>
            <div className="mt-2 text-[13px] text-white/65">{t(`checkout.${pay.data?.method ?? 'UPI'}`, pay.data?.method ?? '')}</div>
            <div className="mt-5 flex items-center gap-2 text-[12.5px] text-white/70">
              <Spinner className="h-4 w-4 text-marigold-500" /> {t('pay.waiting')}
            </div>
          </section>
          {mock ? (
            <section className="mt-4 rounded-[24px] bg-white p-5 shadow-[var(--shadow-card)]">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-marigold-100 text-marigold-700">
                  <FlaskConical className="h-4 w-4" aria-hidden />
                </span>
                <span className="text-[14px] font-bold text-ink-900">{t('pay.testGateway')}</span>
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-ink-600">{t('pay.testNote')}</p>
              <div className="mt-4 flex flex-col gap-2">
                <Button size="lg" variant="accent" block loading={busy === 'success'} onClick={() => void simulate('success')}>
                  <CheckCircle2 className="h-5 w-5" /> {t('pay.simulateSuccess')}
                </Button>
                <Button size="lg" variant="secondary" block loading={busy === 'failure'} onClick={() => void simulate('failure')}>
                  {t('pay.simulateFailure')}
                </Button>
              </div>
            </section>
          ) : (
            // Razorpay checkout.js is loaded only when real credentials are configured (see docs/ENVIRONMENT.md).
            <section className="mt-4 flex items-center gap-3 rounded-[24px] bg-white p-5 shadow-[var(--shadow-card)]">
              <Spinner />
              <p className="text-[13.5px] text-ink-600">{t('pay.waiting')}</p>
            </section>
          )}
        </>
      )}
      <p className="mt-5 flex items-center justify-center gap-1.5 text-[11.5px] text-ink-500">
        <Lock className="h-3.5 w-3.5" aria-hidden /> {t('checkout.secure', 'Secure payment · alcohol is sold by the licensed store')}
      </p>
    </Page>
  );
}

// ---- Orders list ----------------------------------------------------------------------------------------------
function OrderRow({ o }: { o: OrderSummaryDto }) {
  const { t } = useTranslation();
  const active = !CLOSED_STATUSES.includes(o.status);
  const date = new Date(o.placedAt ?? o.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return (
    <Link to={o.status === 'PAYMENT_PENDING' ? `/pay/${o.id}` : `/orders/${o.id}`} className="rb-press flex items-center gap-3 rounded-[22px] bg-white p-3 pr-4 shadow-[var(--shadow-card)]">
      <span className="relative h-14 w-14 shrink-0 overflow-hidden rounded-[16px]">
        <StorefrontArt name={o.storeName} className="h-full w-full" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-bold text-ink-900">{o.storeName}</div>
        <div className="rb-num mt-0.5 text-[12px] text-ink-500">
          {o.code} · {date} · {inr(o.total)}
        </div>
        <div className="mt-1.5">
          {active ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-marigold-100 px-2.5 py-0.5 text-[11.5px] font-semibold text-marigold-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-marigold-600" aria-hidden />
              {t(`status.${o.status}`)}
            </span>
          ) : (
            <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{t(`status.${o.status}`)}</Badge>
          )}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-ink-300" aria-hidden />
    </Link>
  );
}

export function OrdersPage() {
  const { t } = useTranslation();
  const { user, setLoginOpen } = useSession();
  const orders = useAsync((s) => client.get<{ items: OrderSummaryDto[] }>('/orders', s), [user?.id], { enabled: !!user });
  if (!user)
    return (
      <Page>
        <EmptyState icon={<Receipt className="h-8 w-8" />} title={t('auth.loginTitle')} action={<Button onClick={() => setLoginOpen(true)}>{t('common.continue')}</Button>} />
      </Page>
    );
  const items = orders.data?.items ?? [];
  const active = items.filter((o) => !CLOSED_STATUSES.includes(o.status));
  const past = items.filter((o) => CLOSED_STATUSES.includes(o.status));
  return (
    <Page>
      <PageHeader title={t('order.history')} />
      {orders.loading && (
        <div className="mt-4 flex flex-col gap-2.5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[84px] rounded-[22px]" />
          ))}
        </div>
      )}
      {!orders.loading && items.length === 0 && <EmptyState icon={<Receipt className="h-8 w-8" />} title={t('order.noOrders')} />}
      {active.length > 0 && (
        <>
          <div className="rb-eyebrow mb-2.5 mt-5 text-ink-500">{t('order.active', 'In progress')}</div>
          <div className="flex flex-col gap-2.5">
            {active.map((o) => (
              <OrderRow key={o.id} o={o} />
            ))}
          </div>
        </>
      )}
      {past.length > 0 && (
        <>
          <div className="rb-eyebrow mb-2.5 mt-7 text-ink-500">{t('order.past', 'Past orders')}</div>
          <div className="flex flex-col gap-2.5">
            {past.map((o) => (
              <OrderRow key={o.id} o={o} />
            ))}
          </div>
        </>
      )}
    </Page>
  );
}

// ---- Live tracking ------------------------------------------------------------------------------------------
function useOrderRealtime(orderId: string | undefined, userId: string | undefined, onEvent: (e: string, p: unknown) => void) {
  const [connected, setConnected] = useState(false);
  const cb = useRef(onEvent);
  cb.current = onEvent;
  useEffect(() => {
    if (!orderId || !client.accessToken) return;
    let socket: Socket | null = io(apiOrigin() || undefined, { path: '/socket.io', auth: (fn) => fn({ token: client.accessToken }), transports: ['websocket', 'polling'], reconnectionDelayMax: 8000 });
    socket.on('connect', () => {
      setConnected(true);
      socket?.emit('order.subscribe', { orderId });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('auth.error', async () => {
      await client.restore();
      socket?.connect();
    });
    socket.onAny((event, payload) => cb.current(event, payload));
    return () => {
      socket?.disconnect();
      socket = null;
    };
    // Re-run once the session is restored: on a cold start / deep link the access token arrives after first render.
  }, [orderId, userId]);
  return connected;
}

function useNow(ms: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const FAILURE_STATUSES: OrderStatus[] = ['STORE_REJECTED', 'DELIVERY_REFUSED', 'RETURN_TO_STORE', 'RETURNED', 'CANCELLED', 'REFUND_PENDING', 'REFUNDED', 'FAILED'];

/** Segmented progress: one bar per customer-facing step, the current one in amber. */
function ProgressBars({ status }: { status: OrderStatus }) {
  const idx = progressIndex(status);
  return (
    <div className="flex gap-1" aria-hidden>
      {CUSTOMER_PROGRESS_STEPS.map((s, i) => (
        <span key={s.key} className={cx('h-1.5 flex-1 rounded-full', i < idx || status === 'DELIVERED' ? 'bg-ink-900' : i === idx ? 'animate-pulse bg-marigold-500' : 'bg-ink-200')} />
      ))}
    </div>
  );
}

function ago(iso: string, now: number) {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.round(s / 60)} min`;
}

export function OrderPage() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const nav = useNavigate();
  const { config, user } = useSession();
  const toast = useToast();
  const now = useNow(5000);
  const [order, setOrder] = useState<OrderDetailDto | null>(null);
  const [tracking, setTracking] = useState<TrackingSnapshotDto | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, tr] = await Promise.all([client.get<OrderDetailDto>(`/orders/${orderId}`), client.get<TrackingSnapshotDto>(`/orders/${orderId}/tracking`)]);
      setOrder(o);
      setTracking(tr);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = useOrderRealtime(orderId, user?.id, (event, payload) => {
    if (event === 'order.location') {
      const p = payload as RealtimeEvents['order.location'];
      setTracking((tr) => (tr ? { ...tr, runnerLocation: p.location, runnerLocationVisible: true, eta: p.eta ?? tr.eta } : tr));
    } else if (event === 'order.status') {
      const p = payload as RealtimeEvents['order.status'];
      setOrder((o) => (o ? { ...o, status: p.status, prepStatus: p.prepStatus } : o));
      void load();
    }
  });

  // Polling fallback when the socket is down (mobile networks).
  useEffect(() => {
    if (connected || !order || CLOSED_STATUSES.includes(order.status)) return;
    const id = setInterval(() => void load(), 6000);
    return () => clearInterval(id);
  }, [connected, order, load]);

  const back = () => nav('/orders');
  if (error && !order)
    return (
      <Page flow>
        <FlowHeader title={t('order.track')} step="track" onBack={back} />
        <Alert tone="danger">{errorMessage(error)}</Alert>
      </Page>
    );
  if (!order || !tracking)
    return (
      <Page flow className="!pt-0">
        <Skeleton className="-mx-4 h-[44vh] rounded-none" />
        <Skeleton className="mt-4 h-24 rounded-[24px]" />
        <Skeleton className="mt-3 h-40 rounded-[24px]" />
      </Page>
    );

  const closed = CLOSED_STATUSES.includes(order.status);
  const failed = FAILURE_STATUSES.includes(order.status);
  const showMap = !closed && order.status !== 'PAYMENT_PENDING';
  const justPlaced = ['PLACED', 'STORE_CONFIRMATION_PENDING'].includes(order.status);
  const runnerPoint = tracking.runnerLocationVisible && tracking.runnerLocation ? tracking.runnerLocation : null;
  const eta = tracking.eta;

  return (
    <Page flow className="!pt-0">
      {showMap ? (
        <div className="relative -mx-4 h-[46vh] min-h-[300px] max-h-[460px]">
          <LiveMap
            styleUrl={config?.mapStyleUrl ?? 'https://demotiles.maplibre.org/style.json'}
            store={tracking.store}
            destination={tracking.destination}
            runner={runnerPoint}
            route={tracking.route}
            height="100%"
            controls={false}
            fitPadding={{ top: 80, bottom: 70, left: 50, right: 50 }}
          />
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-4">
            <button onClick={back} className="rb-press pointer-events-auto grid h-11 w-11 place-items-center rounded-full bg-white shadow-[var(--shadow-raised)]" aria-label={t('common.back')}>
              <ArrowLeft className="h-5 w-5" />
            </button>
            <span className={cx('pointer-events-auto inline-flex h-9 items-center gap-2 rounded-full px-3.5 text-[12px] font-semibold shadow-[var(--shadow-raised)]', connected ? 'bg-ink-900 text-white' : 'bg-white text-ink-600')}>
              <span className={cx('h-2 w-2 rounded-full', connected ? 'animate-pulse bg-success-500' : 'bg-ink-300')} aria-hidden />
              {connected ? t('common.live') : t('common.reconnecting')}
            </span>
          </div>
        </div>
      ) : (
        <div className="-mx-4 px-4 pt-3">
          <button onClick={back} className="rb-press grid h-10 w-10 place-items-center rounded-full bg-white shadow-[var(--shadow-card)]" aria-label={t('common.back')}>
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className={cx('relative -mx-4 bg-paper px-4', showMap ? '-mt-7 rounded-t-[28px] pt-3 shadow-[0_-12px_30px_rgb(9_14_29/0.12)]' : 'pt-2')}>
        {showMap && <span className="mx-auto mb-3 block h-1 w-10 rounded-full bg-ink-200" aria-hidden />}

        {/* Status + ETA */}
        {closed ? (
          <section className={cx('relative mt-2 overflow-hidden rounded-[28px] p-6 text-white shadow-[var(--shadow-float)]', failed ? 'bg-ink-800' : 'bg-night-900')}>
            {!failed && <span className="pointer-events-none absolute -right-14 -top-14 h-44 w-44 rounded-full bg-marigold-500/25 blur-3xl" aria-hidden />}
            <span className={cx('grid h-14 w-14 place-items-center rounded-full', failed ? 'bg-white/10 text-white' : 'bg-marigold-500 text-ink-950 shadow-[var(--shadow-glow)]')}>
              {failed ? <CircleAlert className="h-7 w-7" aria-hidden /> : <CheckCircle2 className="h-7 w-7" aria-hidden />}
            </span>
            <h1 className="rb-display mt-4 text-[28px] font-extrabold leading-tight">{t(`status.${order.status}`)}</h1>
            <p className="rb-num mt-1 text-[13px] text-white/65">
              {order.code} · {order.storeName}
            </p>
            {order.status === 'DELIVERED' && <p className="mt-3 text-[13px] text-white/80">{t('safety.deliveredLine', 'Thanks for staying in. Enjoy responsibly.')}</p>}
          </section>
        ) : (
          <section className="rounded-[24px] bg-white p-4 shadow-[var(--shadow-card)]">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="rb-eyebrow rb-num truncate text-ink-500">
                  {order.code} · {order.storeName}
                </div>
                <h1 className="rb-display mt-1 text-[23px] font-bold leading-tight text-ink-900">{t(`status.${order.status}`)}</h1>
              </div>
              {eta && (
                <div className="shrink-0 rounded-[18px] bg-night-900 px-3.5 py-2.5 text-center text-white" aria-label={t('order.eta', { minutes: eta.totalMinutes })}>
                  <div className="rb-display rb-num text-[26px] font-extrabold leading-none text-marigold-500">{eta.totalMinutes}</div>
                  <div className="rb-eyebrow mt-1 text-[9px] text-white/60">{t('order.min', 'min')}</div>
                </div>
              )}
            </div>
            <div className="mt-3">
              <ProgressBars status={order.status} />
            </div>
            {eta && <p className="mt-2.5 text-[12px] text-ink-500">{t('order.etaBreakdown', { a: eta.runnerToStoreMinutes, b: eta.preparationMinutes, c: eta.storeToCustomerMinutes })}</p>}
            {tracking.runnerLocation && (
              <p className="rb-num mt-1 text-[11px] text-ink-400" data-testid="runner-coords">
                GPS {tracking.runnerLocation.lat.toFixed(5)}, {tracking.runnerLocation.lng.toFixed(5)} · {t('order.updated', 'updated')} {ago(tracking.runnerLocation.recordedAt, now)} {t('order.ago', 'ago')}
              </p>
            )}
          </section>
        )}

        {justPlaced && (
          <div className="mt-3 flex items-center gap-3 rounded-[20px] bg-success-50 p-3.5 text-success-600">
            <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-[13px] font-semibold">{t('order.placedNote', 'Order confirmed. The licensed store is reviewing it now.')}</span>
          </div>
        )}
        {order.complianceHold && <Alert tone="warning" className="mt-3">{t('order.complianceHold')}</Alert>}

        {order.deliveryOtp && !closed && (
          <section className="mt-3 rounded-[24px] bg-night-900 p-4 text-white">
            <div className="flex items-center justify-between">
              <span className="rb-eyebrow text-marigold-500">{t('order.deliveryCode')}</span>
              <ShieldCheck className="h-5 w-5 text-marigold-500" aria-hidden />
            </div>
            <div className="mt-3 flex gap-2" aria-label={`${t('order.deliveryCode')} ${order.deliveryOtp.split('').join(' ')}`}>
              {order.deliveryOtp.split('').map((d, i) => (
                <span key={i} className="rb-num grid h-14 flex-1 place-items-center rounded-[14px] bg-white/10 text-[26px] font-bold ring-1 ring-white/10">
                  {d}
                </span>
              ))}
            </div>
            <p className="mt-3 text-[12px] leading-relaxed text-white/70">{t('order.deliveryCodeNote')}</p>
            {order.handoverRequirements?.minimumAge && (
              <p className="mt-1.5 text-[12px] font-semibold text-white/90">
                {t('order.keepIdReady', { documents: order.handoverRequirements.acceptedDocuments.map(idLabel).join(', '), age: order.handoverRequirements.minimumAge })}
              </p>
            )}
          </section>
        )}

        {order.runner && !closed && (
          <section className="mt-3 flex items-center gap-3 rounded-[24px] bg-white p-3.5 shadow-[var(--shadow-card)]">
            <span className="rb-display grid h-12 w-12 shrink-0 place-items-center rounded-full bg-ink-900 text-[18px] font-bold text-marigold-500">{order.runner.firstName.slice(0, 1)}</span>
            <div className="min-w-0 flex-1">
              <div className="rb-eyebrow text-ink-500">{t('order.runner')}</div>
              <div className="truncate text-[15px] font-bold text-ink-900">{order.runner.firstName}</div>
              <div className="flex items-center gap-1.5 text-[12px] text-ink-500">
                <Bike className="h-3.5 w-3.5" aria-hidden /> {order.runner.vehicleLabel ?? '—'}
                {order.runner.rating ? (
                  <span className="rb-num inline-flex items-center gap-0.5 font-semibold text-ink-700">
                    · <Star className="h-3 w-3 fill-marigold-500 text-marigold-500" aria-hidden /> {order.runner.rating.toFixed(1)}
                  </span>
                ) : null}
              </div>
            </div>
            <button onClick={() => setChatOpen(true)} className="rb-press grid h-11 w-11 place-items-center rounded-full bg-ink-900 text-white" aria-label={t('order.chat')}>
              <MessageCircle className="h-5 w-5" />
            </button>
          </section>
        )}

        {!closed && (
          <div className="mt-3 flex items-center gap-3 rounded-[20px] bg-white px-4 py-3 ring-1 ring-ink-900/[0.06]">
            <ShieldCheck className="h-5 w-5 shrink-0 text-peacock-600" aria-hidden />
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-ink-900">{t('safety.orderLine1', 'You’re staying put. Good call.')}</div>
              <div className="text-[12px] text-ink-500">{t('safety.orderLine2', 'Don’t drink & drive — your order is coming to you.')}</div>
            </div>
          </div>
        )}

        <section className="mt-3 rounded-[24px] bg-white p-4 shadow-[var(--shadow-card)]">
          <ProgressTimeline status={order.status} history={order.history} />
        </section>

        <section className="mt-3 rounded-[24px] bg-white p-4 shadow-[var(--shadow-card)]">
          <h3 className="rb-eyebrow text-ink-500">{t('order.items')}</h3>
          <ul className="mt-3 flex flex-col gap-2.5">
            {order.items.map((i) => (
              <li key={i.id} className="flex items-center gap-3">
                <span className="h-11 w-11 shrink-0 overflow-hidden rounded-[12px] p-1" style={stageStyle(i.categorySlug)}>
                  <ProductImage url={null} categorySlug={i.categorySlug} alt={i.name} brand={i.brand} volumeMl={i.volumeMl} className="h-full w-full" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-1 text-[13.5px] font-semibold text-ink-900">{i.name}</span>
                  <span className="rb-num text-[11.5px] text-ink-500">
                    {i.quantity} × {inr(i.unitPrice)}
                    {i.volumeMl ? ` · ${volume(i.volumeMl)}` : ''}
                  </span>
                </span>
                <span className="rb-num text-[14px] font-semibold text-ink-900">{inr(i.lineTotal)}</span>
              </li>
            ))}
          </ul>
        </section>
        <div className="mt-3">
          <BillDetails pricing={order.pricing} storeName={order.sellerOfRecord.legalName} />
        </div>
        {order.refunds.length > 0 && (
          <section className="mt-3 rounded-[24px] bg-white p-4 shadow-[var(--shadow-card)]">
            <h3 className="rb-eyebrow text-ink-500">{t('order.refunds')}</h3>
            {order.refunds.map((r) => (
              <div key={r.id} className="mt-2 flex items-center justify-between text-[13.5px]">
                <span className="capitalize text-ink-700">{r.reason.replace(/_/g, ' ').toLowerCase()}</span>
                <span className="rb-num flex items-center gap-2 font-semibold">
                  {inr(r.amount)} <Badge tone={r.status === 'SUCCEEDED' ? 'success' : 'warning'}>{r.status}</Badge>
                </span>
              </div>
            ))}
          </section>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          {order.status === 'DELIVERED' && !order.rating && (
            <Button variant="accent" className="col-span-2" size="lg" onClick={() => setRateOpen(true)}>
              <Star className="h-5 w-5" /> {t('order.rate')}
            </Button>
          )}
          <Link to={`/support?order=${order.id}`} className="contents">
            <Button variant="secondary">
              <LifeBuoy className="h-4 w-4" /> {t('order.help')}
            </Button>
          </Link>
          {['DELIVERED', 'REFUNDED', 'REFUND_PENDING', 'RETURNED', 'CANCELLED'].includes(order.status) && (
            <Link to={`/orders/${order.id}/receipt`} className="contents">
              <Button variant="secondary">
                <Receipt className="h-4 w-4" /> {t('order.receipt')}
              </Button>
            </Link>
          )}
          {order.canCancel && (
            <Button variant="ghost" className="text-danger-600" onClick={() => setCancelOpen(true)}>
              {t('order.cancel')}
            </Button>
          )}
          {closed && (
            <Button variant="primary" className="col-span-2" onClick={() => nav(`/store/${order.storeId}`)}>
              {t('order.orderAgain', 'Order again')} <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <CancelSheet open={cancelOpen} onClose={() => setCancelOpen(false)} orderId={order.id} onDone={() => void load()} />
      <ChatSheet open={chatOpen} onClose={() => setChatOpen(false)} orderId={order.id} />
      <RateSheet
        open={rateOpen}
        onClose={() => setRateOpen(false)}
        orderId={order.id}
        onDone={() => {
          toast('success', t('order.rateThanks'));
          void load();
        }}
      />
    </Page>
  );
}

function CancelSheet({ open, onClose, orderId, onDone }: { open: boolean; onClose: () => void; orderId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const quote = useAsync((s) => client.get<{ cancellable: boolean; refund: { amountMinor: number; lines: { label: string; amount: number }[] } | null }>(`/orders/${orderId}/cancellation-quote`, s), [orderId, open], { enabled: open });
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open={open} onClose={onClose} title={t('order.cancelTitle')}>
      {quote.loading ? (
        <Spinner />
      ) : quote.data?.cancellable ? (
        <>
          <ul className="flex flex-col gap-1 text-sm">
            {quote.data.refund?.lines.map((l) => (
              <li key={l.label} className="flex justify-between">
                <span className="text-ink-600">{l.label}</span>
                <span className="rb-tabular font-semibold">{inr(l.amount)}</span>
              </li>
            ))}
          </ul>
          <Alert tone="info" className="mt-3">{t('order.cancelRefund', { amount: inr(quote.data.refund?.amountMinor ?? 0) })}</Alert>
          <Button
            variant="danger"
            block
            size="lg"
            className="mt-4"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await client.post(`/orders/${orderId}/cancel`, { reason: 'Customer requested cancellation' });
                onClose();
                onDone();
              } catch (e) {
                toast('danger', errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('order.cancel')}
          </Button>
        </>
      ) : (
        <Alert tone="warning">{t('order.cancelNotPossible')}</Alert>
      )}
    </Sheet>
  );
}

function ChatSheet({ open, onClose, orderId }: { open: boolean; onClose: () => void; orderId: string }) {
  const { t } = useTranslation();
  const msgs = useAsync((s) => client.get<{ id: string; senderRole: string; body: string; createdAt: string }[]>(`/orders/${orderId}/messages`, s), [orderId, open], { enabled: open });
  const [text, setText] = useState('');
  const toast = useToast();
  const send = async () => {
    try {
      await client.post(`/orders/${orderId}/messages`, { body: text });
      setText('');
      msgs.reload();
    } catch (e) {
      toast('danger', errorMessage(e));
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t('order.chat')}>
      <p className="text-xs text-ink-500">Your phone number is never shared. Chat is available only while the order is active.</p>
      <div className="mt-3 flex max-h-72 flex-col gap-2 overflow-y-auto">
        {(msgs.data ?? []).map((m) => (
          <div key={m.id} className={cx('max-w-[80%] rounded-[14px] px-3 py-2 text-sm', m.senderRole === 'CUSTOMER' ? 'self-end bg-ink-900 text-white' : 'self-start bg-ink-100 text-ink-900')}>
            {m.body}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-12" placeholder="Gate code, landmark…" maxLength={500} />
        <Button onClick={() => void send()} disabled={!text.trim()}>
          Send
        </Button>
      </div>
    </Sheet>
  );
}

function RateSheet({ open, onClose, orderId, onDone }: { open: boolean; onClose: () => void; orderId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [overall, setOverall] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <Sheet open={open} onClose={onClose} title={t('order.rate')}>
      <div className="flex justify-center gap-2" role="radiogroup" aria-label={t('order.rate')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} role="radio" aria-checked={overall === n} onClick={() => setOverall(n)} aria-label={`${n}`}>
            <Star className={cx('h-10 w-10', n <= overall ? 'fill-marigold-500 text-marigold-500' : 'text-ink-300')} />
          </button>
        ))}
      </div>
      <Textarea className="mt-4" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder="Optional comment" />
      <Button
        className="mt-4"
        block
        size="lg"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await client.post(`/orders/${orderId}/review`, { overall, storeRating: overall, runnerRating: overall, reasons: [], comment: comment || undefined });
            onClose();
            onDone();
          } catch (e) {
            toast('danger', errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('support.submit')}
      </Button>
    </Sheet>
  );
}

// ---- Receipt --------------------------------------------------------------------------------------------------
interface ReceiptDto {
  orderCode: string;
  issuedAt: string;
  retailerInvoice: { sellerOfRecord: string; storeName: string; address: string; gstin: string | null; exciseLicence: string | null; lines: { name: string; quantity: number; unitPrice: number; total: number }[]; itemsTotal: number; note: string; isDemo: boolean };
  runnerBoyStatement: { issuer: string; lines: { label: string; amount: number }[] };
  totalPaid: number;
  refunds: { amount: number; status: string; reason: string }[];
}

export function ReceiptPage() {
  const { orderId } = useParams();
  const r = useAsync((s) => client.get<ReceiptDto>(`/orders/${orderId}/receipt`, s), [orderId]);
  if (r.loading) return <Page><Skeleton className="h-96" /></Page>;
  if (!r.data) return <Page><Alert tone="danger">{errorMessage(r.error)}</Alert></Page>;
  const d = r.data;
  return (
    <Page>
      <PageHeader title={`Receipt · ${d.orderCode}`} subtitle={new Date(d.issuedAt).toLocaleString('en-IN')} actions={<Button variant="secondary" size="sm" onClick={() => window.print()}>Print</Button>} />
      <Card className="mt-4 rounded-[24px] p-5">
        <div className="rb-eyebrow text-ink-500">Retailer invoice summary</div>
        {d.retailerInvoice.isDemo && <Badge tone="accent" className="mt-2">DEMO / NOT A REAL INVOICE</Badge>}
        <div className="mt-2 font-bold">{d.retailerInvoice.sellerOfRecord}</div>
        <div className="text-xs text-ink-500">{d.retailerInvoice.address}</div>
        <div className="text-xs text-ink-500">GSTIN: {d.retailerInvoice.gstin ?? '—'} · Excise licence: {d.retailerInvoice.exciseLicence ?? '—'}</div>
        <table className="mt-4 w-full text-sm">
          <tbody>
            {d.retailerInvoice.lines.map((l) => (
              <tr key={l.name} className="border-t border-ink-100">
                <td className="py-1.5">{l.quantity} × {l.name}</td>
                <td className="rb-tabular py-1.5 text-right">{inr(l.total)}</td>
              </tr>
            ))}
            <tr className="border-t border-ink-200 font-bold">
              <td className="py-2">Items total</td>
              <td className="rb-tabular py-2 text-right">{inr(d.retailerInvoice.itemsTotal)}</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-2 text-xs text-ink-500">{d.retailerInvoice.note}</p>
      </Card>
      <Card className="mt-4 rounded-[24px] p-5">
        <div className="rb-eyebrow text-ink-500">{d.runnerBoyStatement.issuer}</div>
        <table className="mt-2 w-full text-sm">
          <tbody>
            {d.runnerBoyStatement.lines.map((l) => (
              <tr key={l.label} className="border-t border-ink-100">
                <td className="py-1.5">{l.label}</td>
                <td className="rb-tabular py-1.5 text-right">{inr(l.amount, { decimals: true })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="rb-display mt-3 flex justify-between border-t border-ink-200 pt-3 text-[18px] font-bold">
          <span>Total paid</span>
          <span className="rb-tabular">{inr(d.totalPaid, { decimals: true })}</span>
        </div>
      </Card>
    </Page>
  );
}

export function statusLabelKey(s: OrderStatus) {
  return `status.${s}`;
}
